/**
 * Email validation utilities
 * Validates email formats to match AWS SES requirements and common email standards
 */

/**
 * Validates email address format
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Remove whitespace
  email = email.trim();

  // Basic length check
  if (email.length < 3 || email.length > 254) {
    return false;
  }

  // RFC 5322 compliant email regex (more comprehensive than basic patterns)
  // This pattern validates:
  // - Local part (before @): alphanumeric, dots, hyphens, underscores, plus signs
  // - Domain part (after @): valid domain with at least one dot
  // - TLD: at least 2 characters
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(email)) {
    return false;
  }

  // Split email into local and domain parts
  const [localPart, domainPart] = email.split('@');

  // Validate local part (before @)
  if (!localPart || localPart.length > 64) {
    return false;
  }

  // Check for consecutive dots in local part
  if (localPart.includes('..')) {
    return false;
  }

  // Check if local part starts or ends with a dot
  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return false;
  }

  // Validate domain part (after @)
  if (!domainPart || domainPart.length > 253) {
    return false;
  }

  // Check for valid TLD (must have at least one dot and valid extension)
  const domainParts = domainPart.split('.');
  if (domainParts.length < 2) {
    return false;
  }

  // Check TLD length (must be at least 2 characters)
  const tld = domainParts[domainParts.length - 1];
  if (tld.length < 2) {
    return false;
  }

  // Check for invalid patterns in domain
  if (domainPart.includes('..') || domainPart.startsWith('.') || domainPart.endsWith('.')) {
    return false;
  }

  // Check each domain label
  for (const label of domainParts) {
    if (label.length === 0 || label.length > 63) {
      return false;
    }
    // Domain labels can't start or end with hyphen
    if (label.startsWith('-') || label.endsWith('-')) {
      return false;
    }
  }

  return true;
};

/**
 * Get user-friendly error message for invalid email
 * @param {string} email - Email address to validate
 * @returns {string|null} - Error message or null if valid
 */
export const getEmailValidationError = (email) => {
  if (!email || email.trim() === '') {
    return 'Email address is required';
  }

  email = email.trim();

  if (email.length < 3) {
    return 'Email address is too short';
  }

  if (email.length > 254) {
    return 'Email address is too long';
  }

  if (!email.includes('@')) {
    return 'Email address must contain @';
  }

  const [localPart, domainPart] = email.split('@');

  if (!localPart) {
    return 'Email address must have text before @';
  }

  if (!domainPart) {
    return 'Email address must have a domain after @';
  }

  if (!domainPart.includes('.')) {
    return 'Email domain must include a domain extension (e.g., .com)';
  }

  const domainParts = domainPart.split('.');
  const tld = domainParts[domainParts.length - 1];

  if (tld.length < 2) {
    return 'Email domain extension must be at least 2 characters';
  }

  if (!isValidEmail(email)) {
    return 'Please enter a valid email address';
  }

  return null;
};

/**
 * Validates and sanitizes email input
 * @param {string} email - Email address to sanitize
 * @returns {string} - Sanitized email
 */
export const sanitizeEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return '';
  }

  return email.trim().toLowerCase();
};
